import { fetchEntityAttachments } from '@/lib/entity-attachments';
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { useEntityList } from '@/hooks/useEntityList';
import { useEntityComments } from '@/hooks/useEntityComments';
import { exportEntityCsv } from '@/lib/csv';
import { mapMember } from '../../people/members';
import { useUser } from '@/contexts/UserContext';
import { SectionLayout } from '@/components/section-layout';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton, SaveChangesButton, CancelButton } from '@/styles/StandardButtons';
import { Badge } from '@/components/ui/badge';
import { GenericTag } from '@/styles/GenericTag';
import { GenericForm, GenericFormField, RequiredMark, GenericSelect } from '@/styles/GenericForm';
import { GenericAttachmentsSection } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection } from '@/styles/GenericCommentsSection';
import { Trash2, Download, Upload } from 'lucide-react';
import countries from '../../../lib/countries';
import { PROJECT_TYPE_OPTIONS, getProjectTypeLabel } from '@/lib/project-types';
import { trackLeadCreated, trackLeadUpdated, trackLeadViewed, trackButtonClick, trackFormSubmission } from '../../../lib/posthog-events';
import { getApiUrl } from '@/lib/api-config';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CloseButton } from '@/styles/StandardButtons';
import AddressAutocomplete, { AddressSelection } from '@/features/project/info/AddressAutocomplete';
import { cn } from '@/lib/utils';
import { colors } from '@/design-system/colors';
import { uploadLeadAttachments, deleteLeadAttachment, type LeadFileAttachment } from '../store';

interface Lead {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  status: 'active_client' | 'new_lead' | 'contacted' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost';
  lead_source: string;
  other_source: string;
  budget_range: string;
  project_type: string;
  timeline: string;
  notes: string;
  created_at: string;
  attachment_count?: number;
}

export function LeadManagementPage() {
  const { companyId, user } = useUser();
  // The list: ONE server-driven query (search + sort + page) over the company's active leads -- the database searches, sorts and pages
  // everyone the user may see; each lead's document counter comes with the row (hooks/useEntityList.ts).
  const list = useEntityList<any, Lead>({
    entityType: 'lead_member',
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    defaultSort: { columnKey: 'created_at', direction: 'desc', sortKey: 'created_at' },
    mapRow: (row) => {
      const person = mapMember<any>(row);
      return { ...person, status: person.lead_status || 'new_lead', lead_source: person.lead_source || '', other_source: person.other_source || '', budget_range: person.budget_range || '', project_type: person.project_type || '', timeline: person.timeline || '' } as Lead;
    },
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [formData, setFormData] = useState<Partial<Lead>>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip_code: '',
    status: 'new_lead',
    lead_source: '',
    other_source: '',
    budget_range: '',
    project_type: '',
    timeline: '',
    notes: ''
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const originalFormDataRef = useRef<Partial<Lead>>({});
  const uploadLeadAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<LeadFileAttachment[]> => {
    if (!companyId || !user?.id) return [];
    return uploadLeadAttachments(id, files, companyId, user.id);
  }, [companyId, user]);
  const deleteLeadAttachmentFn = useCallback((id: string) => deleteLeadAttachment(id, user?.id), [user]);
  const {
    pendingFiles: selectedFiles,
    uploadedFiles,
    setUploadedFiles,
    handleUpload: handleFileUpload,
    handleRemovePending: handleRemoveSelectedFile,
    handleDelete: handleDeleteFile,
    resetPending: resetSelectedFiles,
    uploadPending: uploadPendingLeadAttachments,
  } = useAttachmentStaging<LeadFileAttachment>({
    entityId: editingLead?.id,
    uploadFn: uploadLeadAttachmentsFn,
    deleteFn: deleteLeadAttachmentFn,
  });

  // Comments (generic architecture, entity_type='lead'): load / add / delete through the API, with their authors.
  const { comments: localComments, addComment: handleAddComment, deleteComment: handleDeleteComment } = useEntityComments('lead', editingLead?.id);

  // Track the page view once, when the first page of leads has loaded
  const viewTracked = useRef(false);
  useEffect(() => {
    if (!list.hasLoaded || viewTracked.current || !companyId) return;
    viewTracked.current = true;
    trackLeadViewed({ lead_count: list.total, company_id: companyId, page: 'lead_management' });
  }, [list.hasLoaded, list.total, companyId]);

  const handleNewLead = () => {
    // Track new lead button click
    trackButtonClick('new_lead_button', { context: 'lead_management' });
    
    setEditingLead(null);
    setError(null); // Clear any previous errors
    resetSelectedFiles();
    setUploadedFiles([]);
    const newFormData: Partial<Lead> = {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company_name: '',
      address: '',
      city: '',
      state: '',
      country: '',
      zip_code: '',
      status: 'new_lead' as Lead['status'],
      lead_source: '',
      other_source: '',
      budget_range: '',
      project_type: '',
      timeline: '',
      notes: ''
    };
    setFormData(newFormData);
    originalFormDataRef.current = JSON.parse(JSON.stringify(newFormData));
    setIsDialogOpen(true);
  };

  const handleEditLead = async (lead: Lead) => {
    setEditingLead(lead);
    setError(null); // Clear any previous errors
    const editFormData = {
      first_name: lead.first_name,
      last_name: lead.last_name,
      email: lead.email,
      phone: lead.phone,
      company_name: lead.company_name,
      address: lead.address,
      city: lead.city,
      state: lead.state,
      country: lead.country,
      zip_code: lead.zip_code,
      status: lead.status,
      lead_source: lead.lead_source,
      other_source: lead.other_source,
      budget_range: lead.budget_range,
      project_type: lead.project_type,
      timeline: lead.timeline,
      notes: lead.notes
    };
    setFormData(editFormData);
    originalFormDataRef.current = JSON.parse(JSON.stringify(editFormData));
    
    // Fetch attachments for this lead
    if (lead.id && companyId) {
      await fetchLeadAttachments(lead.id);
    }
    
    setIsDialogOpen(true);
  };

  const fetchLeadAttachments = async (leadId: string) => {
    if (!companyId) return;
    
    try {
      const attachments: LeadFileAttachment[] = await fetchEntityAttachments('user', leadId, { documentType: 'lead_document', ascending: false });

      setUploadedFiles(attachments);
    } catch (error) {
      console.error('Error fetching lead attachments:', error);
    }
  };


  const parseErrorMessage = (error: any): string => {
    // If it's already a user-friendly string, return it
    if (typeof error === 'string') {
      return error;
    }

    // Check for error object with message
    // API returns { success: false, error: "...", details: "...", code: "..." }
    // When thrown as Error, it becomes { message: "...", ... }
    // Supabase errors can be nested, so check both top-level and nested error objects
    const errorMessage = error?.message || error?.error || '';
    const errorDetails = error?.details || error?.error?.details || '';
    const errorCode = error?.code || error?.error?.code || '';

    // Handle duplicate email errors
    if (
      errorMessage.includes('email') && 
      (errorMessage.includes('unique') || errorMessage.includes('duplicate') || errorCode === '23505')
    ) {
      return 'This email address is already in use. Please use a different email.';
    }

    // Handle unique constraint violations
    if (errorCode === '23505') {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique')) {
        return 'This email address is already in use. Please use a different email.';
      }
      if (errorDetails?.includes('user_id') || errorDetails?.includes('company_id')) {
        return 'This lead is already associated with this company.';
      }
      return 'This record already exists. Please check for duplicates.';
    }

    // Handle not-null constraint violations
    if (errorCode === '23502') {
      const fieldMatch = errorDetails?.match(/column "(\w+)" of relation/);
      if (fieldMatch) {
        const fieldName = fieldMatch[1];
        const friendlyName = fieldName
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase());
        return `${friendlyName} is required.`;
      }
      return 'A required field is missing. Please check all required fields.';
    }

    // Handle foreign key violations
    if (errorCode === '23503') {
      return 'Invalid reference. Please check that all related records exist.';
    }

    // Handle check constraint violations
    if (errorCode === '23514') {
      if (errorMessage.includes('email')) {
        return 'Please enter a valid email address.';
      }
      if (errorMessage.includes('status') || errorMessage.includes('lead_status')) {
        return 'Invalid status value. Please select a valid status.';
      }
      if (errorMessage.includes('role')) {
        return 'Invalid role value. Please select a valid role.';
      }
      return 'Invalid data format. Please check your input.';
    }

    // Handle specific error messages from API
    if (errorMessage.includes('user_id and company_id are required')) {
      return 'Session expired. Please refresh the page and try again.';
    }

    if (errorMessage.includes('Failed to create user relationship')) {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('unique') || detailsStr.includes('users_email_unique')) {
        return 'This email address is already associated with this company.';
      }
      return 'Failed to associate lead with company. Please try again.';
    }

    if (errorMessage.includes('Failed to create user') || errorMessage.includes('Failed to update user')) {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique') || 
          detailsStr.includes('duplicate key') || detailsStr.includes('unique constraint')) {
        return 'Failed to create lead. This email address is already in use. Please use a different email.';
      }
      return 'Failed to save lead. Please check all fields and try again.';
    }

    // Handle generic "Failed to create lead" messages - check if it's a duplicate email
    if (errorMessage.includes('Failed to create lead') || errorMessage.includes('Failed to create')) {
      // Convert details and message to lowercase for case-insensitive matching
      const detailsStr = String(errorDetails || '').toLowerCase();
      const messageStr = String(errorMessage || '').toLowerCase();
      
      // First check if it's a unique constraint violation (code 23505) - this is usually a duplicate
      if (errorCode === '23505') {
        // Check if it's related to email
        if (detailsStr.includes('email') || messageStr.includes('email') || detailsStr.includes('users_email_unique')) {
          return 'Failed to create lead. This email address is already in use. Please use a different email.';
        }
        // Generic unique constraint violation
        return 'Failed to create lead. A record with this information already exists. Please check for duplicates.';
      }
      
      // Check if the error details indicate a duplicate email (even without code)
      // Supabase error message for duplicate email typically contains "users_email_unique" or mentions email
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique') || 
          detailsStr.includes('duplicate key') || detailsStr.includes('unique constraint')) {
        // If it mentions email specifically, it's definitely an email duplicate
        if (detailsStr.includes('email') || detailsStr.includes('users_email_unique')) {
          return 'Failed to create lead. This email address is already in use. Please use a different email.';
        }
      }
      
      // Check nested error object for duplicate email
      if (error?.error) {
        const nestedDetails = String(error.error.details || '').toLowerCase();
        const nestedMessage = String(error.error.message || '').toLowerCase();
        const nestedCode = error.error.code;
        if (nestedCode === '23505' || nestedDetails.includes('email') || nestedMessage.includes('email') || 
            nestedDetails.includes('users_email_unique')) {
          return 'Failed to create lead. This email address is already in use. Please use a different email.';
        }
      }
      
      return 'Failed to create lead. Please check all fields and try again.';
    }

    // Return the original error message if we can't parse it
    return errorMessage || 'An unexpected error occurred. Please try again.';
  };

  const handleSaveLead = async (data: any) => {
    // Validate required fields
    if (!data.first_name?.trim() || !data.last_name?.trim() || !data.email?.trim()) {
      setError('First name, last name, and email are required');
      return;
    }

    // Status is required -- never allow saving it blank/"Select" or null.
    if (!data.status?.trim()) {
      setError('Status is required');
      return;
    }

    // Validate email format
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(data.email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setSaving(true);
    setError(null);

    // Update formData with the data from GenericForm
    const updatedFormData = { ...formData, ...data };
    setFormData(updatedFormData);

    try {
      // 1. The acting user and the company come from the session (the server re-derives both)
      const userId = user?.id;
      if (!userId) throw new Error("User not logged in");
      if (!companyId) throw new Error("No active company found for this user");

      // 3. Insert or update lead in users table
      const userData = {
        first_name: updatedFormData.first_name,
        last_name: updatedFormData.last_name,
        email: updatedFormData.email,
        phone: updatedFormData.phone,
        company_name: updatedFormData.company_name,
        address: updatedFormData.address,
        city: updatedFormData.city,
        state: updatedFormData.state,
        country: updatedFormData.country,
        zip_code: updatedFormData.zip_code,
        lead_status: updatedFormData.status, // Use the new lead_status field
        lead_source: updatedFormData.lead_source,
        other_source: updatedFormData.other_source,
        budget_range: updatedFormData.budget_range,
        project_type: updatedFormData.project_type,
        timeline: updatedFormData.timeline,
        notes: updatedFormData.notes,
      };

      if (editingLead) {
        // Update existing lead (writes to users table, logs as 'lead')
        const response = await fetch(getApiUrl(`api/entities/lead/${editingLead.id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            company_id: companyId,
            ...userData
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          const parsedError = parseErrorMessage(errorData);
          throw new Error(parsedError);
        }

        // Track successful lead update with PostHog
        trackLeadUpdated({
          lead_id: editingLead.id,
          lead_source: updatedFormData.lead_source,
          lead_status: updatedFormData.status,
          project_type: updatedFormData.project_type,
          budget_range: updatedFormData.budget_range,
          timeline: updatedFormData.timeline,
          first_name: updatedFormData.first_name,
          last_name: updatedFormData.last_name,
          email: updatedFormData.email,
          phone: updatedFormData.phone,
          company_name: updatedFormData.company_name,
          city: updatedFormData.city,
          state: updatedFormData.state,
          country: updatedFormData.country,
          company_id: companyId,
          updated_by: userId
        });

        // Track successful form submission
        trackFormSubmission({
          form_type: 'lead_update',
          success: true
        });
      } else {
        // Create new lead (writes to users table, logs as 'lead')
        const response = await fetch(getApiUrl('api/entities/lead'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            company_id: companyId,
            ...userData
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          const parsedError = parseErrorMessage(errorData);
          throw new Error(parsedError);
        }

        const result = await response.json();
        const newUser = result.data;

        // Upload pending files if any -- don't fail the entire save if this
        // fails (uploadPending already logs/reports its own error).
        await uploadPendingLeadAttachments(newUser.id);

        // Track successful lead creation with PostHog
        trackLeadCreated({
          lead_id: newUser.id,
          lead_source: updatedFormData.lead_source,
          lead_status: updatedFormData.status,
          project_type: updatedFormData.project_type,
          budget_range: updatedFormData.budget_range,
          timeline: updatedFormData.timeline,
          first_name: updatedFormData.first_name,
          last_name: updatedFormData.last_name,
          email: updatedFormData.email,
          phone: updatedFormData.phone,
          company_name: updatedFormData.company_name,
          city: updatedFormData.city,
          state: updatedFormData.state,
          country: updatedFormData.country,
          company_id: companyId,
          created_by: userId
        });

        // Track successful form submission
        trackFormSubmission({
          form_type: 'lead_creation',
          success: true
        });

        // NOTE (bug fix, 2026-09-22): this used to also POST
        // api/entities/user_company_relation here to create the lead's
        // membership -- but POST /api/entities/lead already creates that
        // exact membership internally (backend/modules/people/service.js's
        // _createLead, role='client'). The duplicate POST here raced that
        // internal insert against the SAME unique (company_id, email)
        // constraint and always lost with a 23505, which this block then
        // surfaced as a visible error to the user even though the lead had
        // already been created successfully. Removed -- nothing else in
        // this function needs a second membership row.
      }

      // Reset form + refresh leads
      setIsDialogOpen(false);
      setEditingLead(null);
      resetSelectedFiles();
      setUploadedFiles([]);
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        company_name: "",
        address: "",
        city: "",
        state: "",
        country: "",
        zip_code: "",
        status: "new_lead",
        lead_source: "",
        other_source: "",
        budget_range: "",
        project_type: "",
        timeline: "",
        notes: "",
      });
      setError(null);
      list.reload();

    } catch (error: any) {
      console.error("Error saving lead:", error);
      
      // Parse error message for better user experience
      const userFriendlyError = parseErrorMessage(error);
      
      // Track failed lead save with PostHog
      trackFormSubmission({
        form_type: editingLead ? 'lead_update' : 'lead_creation',
        success: false,
        error_message: userFriendlyError
      });

      setError(userFriendlyError);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one lead to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} lead(s)? This action cannot be undone.`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete leads.');
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const leadId of selectedRows) {
      try {
        // Delete lead via generic API
        const response = await fetch(getApiUrl(`api/entities/lead/${leadId}`), {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            company_id: companyId,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error deleting lead:', errorData);
          errorCount++;
          continue;
        }

        successCount++;
      } catch (error) {
        console.error('Error deleting lead:', error);
        errorCount++;
      }
    }

    list.reload();
    setSelectedRows([]);

    if (errorCount > 0) {
      alert(`Deleted ${successCount} lead(s). ${errorCount} failed.`);
    } else {
      alert(`Successfully deleted ${successCount} lead(s).`);
    }
  };

  // Export to CSV: the leads selected in the table, or -- with nothing selected -- EVERY lead the current search matches,
  // read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'leads', 'leads');

  const mapLeadStatusToTag = (status: string): 'active_client' | 'new_lead' | 'contacted' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost' | 'draft' => {
    switch (status?.toLowerCase()) {
      case 'active_client':
        return 'active_client';
      case 'new_lead':
        return 'new_lead';
      case 'contacted':
        return 'contacted';
      case 'qualified':
        return 'qualified';
      case 'proposal':
        return 'proposal';
      case 'negotiation':
        return 'negotiation';
      case 'closed_won':
        return 'closed_won';
      case 'closed_lost':
        return 'closed_lost';
      default:
        return 'draft';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active_client': return 'New Client';
      case 'new_lead': return 'New Lead';
      case 'contacted': return 'Contacted';
      case 'qualified': return 'Qualified';
      case 'proposal': return 'Proposal';
      case 'negotiation': return 'Negotiation';
      // No longer a selectable option (see leadFormFields' `status` field
      // above) -- kept here only so a lead created before this change
      // still renders a real label instead of the raw 'closed_won' slug.
      case 'closed_won': return 'Closed Won';
      case 'closed_lost': return 'Closed Lost';
      default: return status;
    }
  };

  const getBudgetLabel = (budget: string) => {
    switch (budget) {
      case 'under_10k': return 'Under $10,000';
      case '10k_25k': return '$10,000 - $25,000';
      case '25k_50k': return '$25,000 - $50,000';
      case '50k_100k': return '$50,000 - $100,000';
      case '100k_250k': return '$100,000 - $250,000';
      case '250k_500k': return '$250,000 - $500,000';
      case 'over_500k': return 'Over $500,000';
      default: return budget || '-';
    }
  };

  const getTimelineLabel = (timeline: string) => {
    switch (timeline) {
      case 'immediate': return 'Immediate';
      case '1_month': return 'Within 1 Month';
      case '1_3_months': return '1-3 Months';
      case '3_6_months': return '3-6 Months';
      case '6_12_months': return '6-12 Months';
      case 'flexible': return 'Flexible';
      default: return timeline || '-';
    }
  };

  // Define form fields for GenericForm
  const leadFormFields = useMemo<GenericFormField[]>(() => {
    const baseFields: GenericFormField[] = [
      // Row 1
      { name: 'first_name', label: 'First Name', type: 'text', placeholder: 'Enter first name', required: true, row: 1 },
      { name: 'last_name', label: 'Last Name', type: 'text', placeholder: 'Enter last name', required: true, row: 1 },
      // Row 2
      { name: 'email', label: 'Email', type: 'email', placeholder: 'Enter email', required: true, row: 2 },
      { name: 'phone', label: 'Phone', type: 'text', placeholder: 'Enter phone number', row: 2 },
      // Row 3 - Company Name, Status, Lead Source, Other Source (1/4 width each)
      { name: 'company_name', label: 'Company Name', type: 'text', placeholder: 'Enter company name', row: 3, colSpan: 0.5 },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        row: 3,
        colSpan: 0.5,
        // Order/content per product decision (2026-09-22): "Closed Won"
        // removed -- conceptually replaced by "New Client" (the existing
        // 'active_client' value, just relabeled here; the value itself is
        // untouched since people/clients/page.tsx already queries
        // `lead_status = 'active_client'` to populate People > Clients,
        // and this same page's own fetchLeads excludes it with
        // `.neq('lead_status', 'active_client')` -- renaming the VALUE
        // would be a data migration, not a label change, and wasn't what
        // was asked here). "New Client" sits right before "Closed Lost".
        options: [
          { label: 'New Lead', value: 'new_lead' },
          { label: 'Contacted', value: 'contacted' },
          { label: 'Qualified', value: 'qualified' },
          { label: 'Proposal', value: 'proposal' },
          { label: 'Negotiation', value: 'negotiation' },
          { label: 'New Client', value: 'active_client' },
          { label: 'Closed Lost', value: 'closed_lost' },
        ]
      },
      {
        name: 'lead_source',
        label: 'Lead Source',
        type: 'select',
        row: 3,
        colSpan: 0.5,
        placeholder: 'Select',
        options: [
          { label: 'Website', value: 'website' },
          { label: 'Referral', value: 'referral' },
          { label: 'Social Media', value: 'social_media' },
          { label: 'Advertising', value: 'advertising' },
          { label: 'Cold Call', value: 'cold_call' },
          { label: 'Trade Show', value: 'trade_show' },
          { label: 'Other', value: 'other' },
        ]
      },
      // Row 4 - Project Type, Budget Range, Timeline (1/4 width each, empty space at end)
      {
        name: 'project_type',
        label: 'Project Type',
        type: 'select',
        row: 4,
        colSpan: 0.5,
        placeholder: 'Select',
        options: PROJECT_TYPE_OPTIONS,
      },
      {
        name: 'budget_range',
        label: 'Budget Range',
        type: 'select',
        row: 4,
        colSpan: 0.5,
        placeholder: 'Select',
        options: [
          { label: 'Under $10,000', value: 'under_10k' },
          { label: '$10,000 - $25,000', value: '10k_25k' },
          { label: '$25,000 - $50,000', value: '25k_50k' },
          { label: '$50,000 - $100,000', value: '50k_100k' },
          { label: '$100,000 - $250,000', value: '100k_250k' },
          { label: '$250,000 - $500,000', value: '250k_500k' },
          { label: 'Over $500,000', value: 'over_500k' },
        ]
      },
      {
        name: 'timeline',
        label: 'Timeline',
        type: 'select',
        row: 4,
        colSpan: 0.5,
        placeholder: 'Select',
        options: [
          { label: 'Immediate', value: 'immediate' },
          { label: 'Within 1 Month', value: '1_month' },
          { label: '1-3 Months', value: '1_3_months' },
          { label: '3-6 Months', value: '3_6_months' },
          { label: '6-12 Months', value: '6_12_months' },
          { label: 'Flexible', value: 'flexible' },
        ]
      },
      // Row 5 - Address (full width) - will be replaced with AddressAutocomplete in customLayout
      { name: 'address', label: 'Address', type: 'text', placeholder: 'Start typing address', row: 5, colSpan: 2 },
      // Row 6 - City, State, Country, ZIP Code (1/4 width each)
      { name: 'city', label: 'City', type: 'text', placeholder: 'Enter city', row: 6, colSpan: 0.5 },
      { name: 'state', label: 'State', type: 'text', placeholder: 'Enter state', row: 6, colSpan: 0.5 },
      {
        name: 'country',
        label: 'Country',
        type: 'select',
        row: 6,
        colSpan: 0.5,
        placeholder: 'Select',
        options: countries.map(country => ({ label: country, value: country }))
      },
      { name: 'zip_code', label: 'ZIP Code', type: 'text', placeholder: 'Enter ZIP code', row: 6, colSpan: 0.5 },
      // Row 7 - Notes (full width, textarea)
      { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Enter any additional notes about this lead', row: 7, colSpan: 2 },
    ];

    // Conditionally add other_source field if lead_source is 'other' (in row 3)
    if (formData.lead_source === 'other') {
      const leadSourceIndex = baseFields.findIndex(f => f.name === 'lead_source');
      if (leadSourceIndex !== -1) {
        baseFields.splice(leadSourceIndex + 1, 0, {
          name: 'other_source',
          label: 'Other Source Details',
          type: 'text',
          placeholder: 'Please specify the source',
          row: 3,
          colSpan: 0.5
        });
      }
    }

    return baseFields;
  }, [formData.lead_source]);

  // Helper function to check if form data has changed
  const hasFormChanged = (current: Partial<Lead>, original: Partial<Lead>): boolean => {
    const allKeys = Array.from(new Set([...Object.keys(current), ...Object.keys(original)]));
    for (const key of allKeys) {
      const currentValue = current[key as keyof Lead];
      const originalValue = original[key as keyof Lead];
      const currentStr = currentValue == null ? '' : String(currentValue).trim();
      const originalStr = originalValue == null ? '' : String(originalValue).trim();
      if (currentStr !== originalStr) {
        return true;
      }
    }
    return false;
  };

  const handleCancelForm = () => {
    const isDirty = hasFormChanged(formData, originalFormDataRef.current);
    if (isDirty) {
      if (window.confirm("Are you sure you want to lose all unsaved progress?")) {
        setIsDialogOpen(false);
        setEditingLead(null);
        setError(null);
        // Reset form data to original
        setFormData(JSON.parse(JSON.stringify(originalFormDataRef.current)));
      }
    } else {
      setIsDialogOpen(false);
      setEditingLead(null);
      setError(null);
    }
  };

  const handleFieldChange = (name: string, value: any, formData: Record<string, any>) => {
    // Clear other_source if lead_source changes away from 'other'
    if (name === 'lead_source' && value !== 'other') {
      formData.other_source = '';
    }
    // Update parent formData to keep it in sync for conditional fields
    setFormData(formData);
  };

  const handleAddressChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      address: value
    }));
  };

  // Helper function to match country from API to our countries list
  const matchCountry = (apiCountry: string | undefined): string | undefined => {
    if (!apiCountry) return undefined;
    
    // Try exact match first
    const exactMatch = countries.find(c => c.toLowerCase() === apiCountry.toLowerCase());
    if (exactMatch) return exactMatch;
    
    // Try common variations
    const countryVariations: Record<string, string> = {
      'us': 'United States',
      'usa': 'United States',
      'united states of america': 'United States',
      'uk': 'United Kingdom',
      'united kingdom of great britain and northern ireland': 'United Kingdom',
      'uae': 'United Arab Emirates',
      'south korea': 'South Korea',
      'north korea': 'North Korea',
      'russia': 'Russia',
      'russian federation': 'Russia',
    };
    
    const normalized = apiCountry.toLowerCase().trim();
    if (countryVariations[normalized]) {
      return countryVariations[normalized];
    }
    
    // Try partial match (e.g., "United States" matches "United States of America")
    const partialMatch = countries.find(c => 
      c.toLowerCase().includes(normalized) || normalized.includes(c.toLowerCase())
    );
    if (partialMatch) return partialMatch;
    
    return undefined;
  };

  const handleAddressSelect = (selection: AddressSelection) => {
    const matchedCountry = matchCountry(selection.country);
    setFormData(prev => ({
      ...prev,
      address: selection.addressLine1,
      city: selection.city || prev.city,
      state: selection.state || prev.state,
      zip_code: selection.postalCode || prev.zip_code,
      ...(matchedCountry && { country: matchedCountry }),
    }));
  };

  // Define table columns
  const columns: GenericTableColumn<Lead>[] = [
    {
      key: 'first_name',
      header: 'Name',
      width: '11.25rem', // 180px = 11.25rem
      sortable: true,
      sortKey: 'name',
      render: (row) => (
        <div className="font-medium">
          {row.first_name} {row.last_name}
          </div>
      ),
    },
    {
      key: 'email',
      header: 'Contact',
      width: '12rem',
      sortable: true,
      sortKey: 'email',
      render: (row) => (
        <div>
          <div>{row.email}</div>
          {row.phone && <div className="text-xs text-gray-500">{row.phone}</div>}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '7.5rem',
      sortable: true,
      sortKey: 'lead_status',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapLeadStatusToTag(row.status)}
        >
          {getStatusLabel(row.status)}
        </GenericTag>
      ),
    },
    {
      key: 'project_type',
      header: 'Project Type',
      width: '7.5rem', // 140px = 8.75rem
      sortable: true,
      sortKey: 'project_type',
      render: (row) => getProjectTypeLabel(row.project_type),
    },
    {
      key: 'budget_range',
      header: 'Budget',
      width: '9rem',
      sortable: true,
      sortKey: 'budget_range',
      render: (row) => getBudgetLabel(row.budget_range),
    },
    {
      key: 'timeline',
      header: 'Timeline',
      width: '7rem',
      sortable: true,
      sortKey: 'timeline',
      render: (row) => getTimelineLabel(row.timeline),
    },
    {
      key: 'created_at',
      header: 'Created',
      width: '7rem',
      sortable: true,
      sortKey: 'created_at',
      render: (row) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      key: 'attachment_count',
      header: 'Attachments',
      sortable: true,
      sortKey: 'attachment_count',
      width: '6.25rem',
      align: 'center' as const,
      render: (row) => row.attachment_count || 0,
    },
  ];

  const menuItems = [
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => console.log('Import clicked'),
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
  ];

  if (!list.hasLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <SectionLayout title="Lead Management">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <NewItemButton
              label="New"
              onClick={handleNewLead}
            />
            <SearchButton
              onSearch={list.setSearchInput}
              placeholder="Search"
            />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          columns={columns}
          {...list.tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={1200}
          onRowClick={handleEditLead}
          selectable={true}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
        />

        <GenericForm
          isOpen={isDialogOpen}
          title="Lead"
          mode={editingLead ? 'edit' : 'create'}
          fields={leadFormFields}
          initialValues={formData}
          onSubmit={handleSaveLead}
          onCancel={handleCancelForm}
          onFieldChange={handleFieldChange}
          error={error}
          submitButtonText={editingLead ? 'Update Lead' : 'Create Lead'}
          saving={saving}
          customLayout={(() => {
            // Helper to render a field
            const renderField = (field: GenericFormField) => {
              const fieldValue = (formData as Record<string, any>)[field.name] || '';
              if (field.type === 'select') {
                return (
                  <GenericSelect
                    value={String(fieldValue)}
                    onValueChange={(value) => handleFieldChange(field.name, value, { ...formData, [field.name]: value })}
                    disabled={field.disabled}
                    required={field.required}
                    placeholder={field.placeholder}
                    contentClassName={field.name === 'country' ? "max-h-[18.75rem] overflow-y-auto" : ""}
                    options={field.options || []}
                  />
                );
              } else if (field.type === 'textarea') {
                return (
                  <Textarea
                    id={field.name}
                    value={String(fieldValue)}
                    onChange={(e) => handleFieldChange(field.name, e.target.value, { ...formData, [field.name]: e.target.value })}
                    placeholder={field.placeholder}
                    disabled={field.disabled}
                  />
                );
              } else {
                return (
                  <Input
                    id={field.name}
                    type={field.type}
                    value={String(fieldValue)}
                    onChange={(e) => handleFieldChange(field.name, e.target.value, { ...formData, [field.name]: e.target.value })}
                    placeholder={field.placeholder}
                    className="font-form-input generic-form-field"
                    disabled={field.disabled}
                  />
                );
              }
            };

            // Group fields by row
            const fieldsByRow = leadFormFields.reduce((acc, field) => {
              const row = field.row || 999;
              if (!acc[row]) acc[row] = [];
              acc[row].push(field);
              return acc;
            }, {} as Record<number, GenericFormField[]>);

            const sortedRows = Object.keys(fieldsByRow).sort((a, b) => Number(a) - Number(b));

            return (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSaveLead(formData);
                }}
                className={cn("generic-form-container", "w-[80vw]")}
                style={{ maxHeight: 'var(--form-max-height-default)' }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div 
                  className={cn("generic-form-header", "generic-form-header-default")}
                  style={{ backgroundColor: colors.formHeaderFooter }}
                >
                  <h2 className="font-form-title">
                    {editingLead ? 'Edit Lead' : 'Create Lead'}
                  </h2>
                  <CloseButton onClick={handleCancelForm} />
                </div>

                {/* Body */}
                <div className={cn("generic-form-body", "generic-form-body-default")}>
                  {error && (
                    <div className="generic-form-error">
                      {error}
                    </div>
                  )}
                  <div className="generic-form-body-spacing-default">
                    {/* Row 1: First Name, Last Name */}
                    <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--field-gap-default)' }}>
                      {fieldsByRow[1]?.map((field) => (
                        <div key={field.name} className="space-y-2">
                          <Label htmlFor={field.name} className="font-form-label">
                            {field.label} <RequiredMark required={field.required} />
                          </Label>
                          {renderField(field)}
                        </div>
                      ))}
                    </div>

                    {/* Row 2: Email, Phone */}
                    <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--field-gap-default)' }}>
                      {fieldsByRow[2]?.map((field) => (
                        <div key={field.name} className="space-y-2">
                          <Label htmlFor={field.name} className="font-form-label">
                            {field.label} <RequiredMark required={field.required} />
                          </Label>
                          {renderField(field)}
                        </div>
                      ))}
                    </div>

                    {/* Row 3: Company Name, Status, Lead Source, Other Source (1/4 width each) */}
                    <div className="grid grid-cols-1 md:grid-cols-4" style={{ gap: 'var(--field-gap-default)' }}>
                      {fieldsByRow[3]?.map((field) => (
                        <div key={field.name} className="space-y-2">
                          <Label htmlFor={field.name} className="font-form-label">
                            {field.label} <RequiredMark required={field.required} />
                          </Label>
                          {renderField(field)}
                        </div>
                      ))}
                    </div>

                    {/* Row 4: Project Type, Budget Range, Timeline (1/4 width each, empty space at end) */}
                    <div className="grid grid-cols-1 md:grid-cols-4" style={{ gap: 'var(--field-gap-default)' }}>
                      {fieldsByRow[4]?.map((field) => (
                        <div key={field.name} className="space-y-2">
                          <Label htmlFor={field.name} className="font-form-label">
                            {field.label} <RequiredMark required={field.required} />
                          </Label>
                          {renderField(field)}
                        </div>
                      ))}
                    </div>

                    {/* Row 5: Address (full width) with AddressAutocomplete */}
                    <div className="space-y-2">
                      <Label htmlFor="address" className="font-form-label">
                        Address
                      </Label>
                      <AddressAutocomplete
                        value={formData.address || ''}
                        onChange={handleAddressChange}
                        onSelectSuggestion={handleAddressSelect}
                        placeholder="Start typing address"
                        inputClassName="w-full"
                        initialSelected={Boolean(editingLead)}
                      />
                    </div>

                    {/* Row 6: City, State, Country, ZIP Code (1/4 width each) */}
                    <div className="grid grid-cols-1 md:grid-cols-4" style={{ gap: 'var(--field-gap-default)' }}>
                      {fieldsByRow[6]?.map((field) => (
                        <div key={field.name} className="space-y-2">
                          <Label htmlFor={field.name} className="font-form-label">
                            {field.label} <RequiredMark required={field.required} />
                          </Label>
                          {renderField(field)}
                        </div>
                      ))}
                    </div>

                    {/* Row 7: Notes (full width) */}
                    {fieldsByRow[7]?.map((field) => (
                      <div key={field.name} className="space-y-2">
                        <Label htmlFor={field.name} className="font-form-label">
                          {field.label} <RequiredMark required={field.required} />
                        </Label>
                        {renderField(field)}
                      </div>
                    ))}

                    {/* Attachments Section -- available in both create and edit
                        mode. In create mode, files are held in `selectedFiles`
                        (see handleFileUpload) and uploaded only after the lead
                        is actually created (see the create-lead branch of
                        handleSaveLead), once a real user id exists to attach
                        them to. */}
                    <div className="space-y-2">
                      <GenericAttachmentsSection
                        attachments={uploadedFiles.map(file => ({
                          id: file.id,
                          file_name: file.file_name,
                          file_type: file.file_type,
                          file_size: file.file_size,
                          file_url: file.file_url,
                          created_at: file.created_at,
                          created_by: file.created_by,
                          created_by_user: file.created_by_user
                        }))}
                        pendingFiles={selectedFiles}
                        onUpload={handleFileUpload}
                        onDelete={handleDeleteFile}
                        onRemovePending={handleRemoveSelectedFile}
                        mode={editingLead ? 'edit' : 'create'}
                        projectId={null}
                      />
                    </div>

                    {/* Comments Section (generic architecture, entity_type='lead') */}
                    <div className="mt-6">
                      <GenericCommentsSection
                        comments={localComments.map(comment => ({
                          id: comment.id,
                          content: comment.content,
                          created_at: comment.created_at,
                          created_by: comment.created_by,
                          author: comment.author
                        }))}
                        onAddComment={handleAddComment}
                        onDeleteComment={handleDeleteComment}
                        mode={editingLead ? 'edit' : 'create'}
                        placeholder={editingLead ? 'Add a comment...' : 'Comments are available after creating the lead'}
                      />
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div 
                  className={cn("generic-form-footer", "generic-form-footer-default")}
                  style={{ backgroundColor: colors.formHeaderFooter }}
                >
                  <div className="flex justify-end gap-3">
                    <CancelButton
                      onClick={handleCancelForm}
                      disabled={saving}
                    >
                      Cancel
                    </CancelButton>
                    <SaveChangesButton
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </SaveChangesButton>
                  </div>
                </div>
              </form>
            );
          })()}
        />

      </div>
    </SectionLayout>
  );
}
